import { rumAction, type RumActionContext } from "@/lib/datadog-rum";

/**
 * Kory in the ⌘K palette: how typed text is read, what an answer's source line says,
 * when the "Tab to ask Kory" hint retires, and the palette's product events.
 */

/** What the typed text reads as. `null`: a name or a page, so results keep today's order and Ask sits last. */
export type AskShape = "question" | "request" | "write" | null;

const QUESTION_WORDS = new Set([
  "who", "whos", "what", "whats", "when", "where", "why", "how", "hows", "which",
  "is", "are", "am", "was", "were", "do", "does", "did", "can", "could", "should",
  "would", "will", "has", "have", "any",
]);
// Asks Kory to find or explain something: answered, never written.
const REQUEST_VERBS = new Set([
  "show", "list", "summarize", "summarise", "explain", "tell", "give", "compare", "check", "help", "find",
]);
// Asks Kory to make or change something: never done from the palette, only proposed in the tray.
const WRITE_VERBS = new Set([
  "draft", "write", "send", "email", "reply", "schedule", "book", "create", "add", "cancel", "remind",
  "move", "update", "set", "assign", "file", "log", "invite", "post",
]);
// Words before the verb in "can you draft…", "please send…", "i need to add…".
const OPENERS = new Set(["please", "kory", "hey", "can", "could", "would", "will", "you", "i", "we", "need", "want", "to", "lets"]);

/**
 * A client heuristic, no model call: a question mark or a question word reads as a question,
 * an imperative verb as a request (a write when it would make or change something). A single
 * word never does, so a page called "Schedule" or a customer called "Check" still navigates first.
 */
export function askShape(text: string): AskShape {
  const words = text.trim().toLowerCase().replace(/['’]/g, "").split(/\s+/).filter(Boolean);
  if (!words.length) return null;
  const verb = words.find((w) => !OPENERS.has(w));
  // "can you send Greg the agenda?" and "send Greg the agenda?" are still writes.
  if (text.trim().endsWith("?")) return verb && WRITE_VERBS.has(verb) ? "write" : "question";
  if (words.length < 2) return null;
  if (verb && WRITE_VERBS.has(verb)) return "write";
  if (verb && REQUEST_VERBS.has(verb)) return "request";
  if (QUESTION_WORDS.has(words[0])) return "question";
  return null;
}

/** The answer's source line: the fast tools it read, else the day's snapshot. */
const SOURCES: Record<string, string> = {
  read_feed: "your tray",
  lookup_person: "the directory",
  omnisearch: "search",
  list_my_todos: "your to-dos",
  list_my_rocks: "your rocks",
  list_my_measurables: "your measurables",
  calendar_list_events: "your calendar",
  calendar_find_free: "your calendar",
  lookup_ticket: "Linear",
  get_kpi: "the KPI registry",
  search_kpis: "the KPI registry",
  search_business_definitions: "Business Definitions",
  get_business_definition: "Business Definitions",
  evaluate_business_definition: "Business Definitions",
  list_meeting_teams: "your meetings",
  props_moments: "Props",
  read_page: "this page",
};

export function answerSource(tools: string[], cached: boolean): string {
  if (cached) return "From earlier today";
  const names = [...new Set(tools.map((t) => SOURCES[t] ?? t.replaceAll("_", " ")))];
  return names.length ? `From ${names.join(" and ")}` : "From your day so far";
}

/** The hint retires once someone has asked this many times. */
export const HINT_RETIRES_AFTER = 3;
export const ASK_COUNT_KEY = "kory.palette.asks";

export function hintShows(asks: number): boolean {
  return asks < HINT_RETIRES_AFTER;
}

/** A picked result, as the tray's "Ask about" chip carries it. */
export interface AskAboutEntity {
  label: string;
  /** An omnisearch type, or "page". */
  type: string;
  id: string | null;
  url: string;
}

/** The model-only line a send carries while the chip is on. Never shown as typed. */
export function askAboutContextLine(entity: AskAboutEntity): string {
  const id = entity.id ? ` (id ${entity.id})` : "";
  return `The person is asking about this ${entity.type}: ${entity.label}${id}, ${entity.url}`;
}

/**
 * The palette's product events, as RUM custom actions (`kory_palette.<name>`). Ids, counts and
 * enums only: never what someone typed or a result's title. `ms` on pick and handoff counts from
 * the first keystroke of the palette session, the guardrail on today's navigation speed.
 */
export type PaletteAskVia = "tab" | "enter" | "row" | "auto";
export type PaletteHandoffVia = "enter" | "tab_tab" | "cmd_j" | "button" | "ask_about";

type PaletteEvents = {
  open: Record<string, never>;
  ask_shown: { shape: string; on_top: boolean; results: number };
  ask: { via: PaletteAskVia; shape: string; chars: number };
  answer: { outcome: string; ms: number };
  answer_cancel: { reason: "pick" | "close" | "edit" };
  handoff: { via: PaletteHandoffVia; target: "tray" | "chat"; answered: boolean; ms: number | null };
  pick: { kind: string; position: number | null; ms: number | null; shape: string };
  ask_about: { kind: string; target: "tray" | "chat" };
  chip_click: { kind: string };
  chip_dismiss: { kind: string };
};

export function paletteEvent<K extends keyof PaletteEvents>(
  name: K,
  context: PaletteEvents[K],
  scene: { viewport: "phone" | "desktop" },
): void {
  rumAction(`kory_palette.${name}`, { ...context, ...scene } satisfies RumActionContext);
}
